import { GATEWAY_BASE_URL } from '../config/endpoints.ts';
import { useT } from '../i18n/index.ts';

/**
 * Hero-right terminal card. A stylised curl invocation + streaming
 * response that shows — at a glance — exactly how thin the integration
 * is: "swap the Base URL, keep your SDK".
 *
 * Colours used inside the dark card come from the `terminal.*` Tailwind
 * sub-palette (see DESIGN.md §6.2 and `tailwind.config.ts`). The warm-
 * linen palette deliberately does NOT contain these hexes — they are a
 * conventional terminal highlight scheme scoped to this component.
 *
 * Token / cost placeholders are representative, not live telemetry. If
 * we ever wire real metrics we replace the `.tokens` / `.cost` template
 * values and call that out here.
 */
export function Terminal(): JSX.Element {
  const t = useT();
  const baseUrl = GATEWAY_BASE_URL.replace(/^https?:\/\//, '');
  return (
    <div className="relative">
      {/* Stacked parchment backdrops — siblings rotated in opposite directions. */}
      <div className="absolute -inset-3 -z-10 rotate-[1.5deg] rounded-[28px] bg-cream" />
      <div className="absolute -inset-1 -z-10 -rotate-[0.8deg] rounded-[24px] bg-butter" />

      <div
        className="relative overflow-hidden rounded-2xl bg-terminal-bg shadow-warm-xl"
        data-testid="hero-terminal"
      >
        {/* Header bar */}
        <div className="flex items-center justify-between border-b border-white/[0.04] bg-terminal-panel px-4 py-3">
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-terminal-dot-red/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-terminal-dot-amber/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-terminal-dot-green/70" />
          </div>
          <span className="font-mono text-[11px] tracking-wide text-terminal-muted">
            {t('hero.terminal.filename')}
          </span>
          <span className="font-mono text-[10px] tracking-widest text-terminal-muted/60">
            {t('hero.terminal.p50')}
          </span>
        </div>

        {/* Body */}
        <div className="p-5 font-mono text-[12.5px] leading-[1.75]">
          <div className="text-terminal-muted">{t('hero.terminal.comment')}</div>
          <div className="mt-2 text-terminal-fg">
            <span className="text-claude">$</span> curl{' '}
            <span className="text-terminal-url">{`https://${baseUrl}/v1/messages`}</span> \
          </div>
          <div className="pl-4 text-terminal-fg">
            -H <span className="text-terminal-string">&quot;x-api-key: $NDT_KEY&quot;</span> \
          </div>
          <div className="pl-4 text-terminal-fg">
            -H{' '}
            <span className="text-terminal-string">
              &quot;anthropic-version: 2023-06-01&quot;
            </span>{' '}
            \
          </div>
          <div className="overflow-hidden text-ellipsis whitespace-nowrap pl-4 text-terminal-fg">
            -d{' '}
            <span className="text-terminal-string">
              &apos;{`{"model":"claude-sonnet-4-5", ... }`}&apos;
            </span>
          </div>

          <div className="mt-4 space-y-1.5 border-t border-white/[0.05] pt-3">
            <div className="text-[11px] uppercase tracking-wider text-terminal-muted">
              {t('hero.terminal.streaming_label')}
            </div>
            <div className="text-terminal-fg">
              {t('hero.terminal.response')}
              <span className="blink ml-0.5 inline-block h-[14px] w-[7px] bg-claude align-[-3px]" />
            </div>
            <div className="flex items-center justify-between pt-3 font-mono text-[10.5px] tracking-wide text-terminal-muted">
              <span>
                {t('hero.terminal.meta.model')}:{' '}
                <span className="text-terminal-url">claude-sonnet-4-5</span>
              </span>
              <span>{t('hero.terminal.meta.tokens', { in: 38, out: 217 })}</span>
              <span>{t('hero.terminal.meta.cost', { cost: '0.0042' })}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
